import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { db } from '../firebaseConfig';
import { apiGet, isBackendConfigured } from '../utils/backendApi';
import EngineeringFlow, { FlowLegend } from '../components/EngineeringFlow';
import { DIAGRAMS, DIAGRAM_GROUPS, diagramById, diagramsForSection } from '../data/engineeringDiagrams';
import './AdminBrain.css';
import './AdminEngineering.css';

// Engineering Handbook (/admin/engineering) — the super-admin reference for
// the whole platform. Three layers, one page:
//   Books      the platform / protocols / App Kit books at full fidelity
//              (engineeringDocs, one doc per section, written by
//              scripts/sync-engineering.mjs; figures via presigned URLs from
//              GET /admin/engineering/figures)
//   Diagrams   React Flow system diagrams from data/engineeringDiagrams.js —
//              click a node for what it is, its files, config keys, brain pages
//   Reference  generated from the code so it cannot drift: routes + gates,
//              workers, every live-config key, Firestore collections + rules,
//              entities, console modules, scripts, code map, SPA routes
// Readers: founders + config/app docs.engineeringReaders (rules helper
// engineeringReader(); the API mirrors it).

const BASE = '/admin/engineering';
const slugify = (h) => String(h).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60);
const fmtAgo = (ms) => {
  if (!ms) return '—';
  const s = Math.max(0, Math.round((Date.now() - ms) / 1000));
  if (s < 90) return `${s}s ago`;
  if (s < 5400) return `${Math.round(s / 60)}m ago`;
  if (s < 172800) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
};
const BRAIN_PATH = '/admin/brain/page/';
const wikify = (md) => String(md || '').replace(/\[\[([^\]|]+)(?:\|([^\]]+))?\]\]/g, (_m, s, l) => `[${l || s}](${BRAIN_PATH}${s.trim()})`);

const REFERENCE_TABS = [
  ['routes', 'API routes', 'Every Express route with the auth gate detected in its handler.'],
  ['workers', 'Workers', 'Everything backend/index.js starts at boot, with its boot condition.'],
  ['liveConfigKeys', 'Live config', 'Every key in CONFIG_DEFAULTS with its default and the comment that documents it.'],
  ['collections', 'Collections & rules', 'Every Firestore match block with its read / write conditions.'],
  ['entities', 'Entities', 'The registry: paths, fields, roles, entitlements.'],
  ['modules', 'Console modules', 'The console catalog: routes, tags, how-to tasks and the agent that handles them.'],
  ['frontendRoutes', 'SPA routes', 'Every <Route> in App.js and whether it sits behind RequireAuth.'],
  ['scripts', 'Scripts', 'scripts/ with each file’s header comment.'],
  ['codeMap', 'Code map', 'Header comments for every backend and frontend source file.'],
];
const REF_DOC = { routes: 'routes', workers: 'workers', liveConfigKeys: 'liveConfig', collections: 'collections', entities: 'entities', modules: 'modules', frontendRoutes: 'frontendRoutes', scripts: 'scripts', codeMap: 'codeMap' };

// ---- Data -----------------------------------------------------------------

function useHandbook() {
  const [state, setState] = useState({ loading: true, error: '', summary: null, docs: [], figures: {}, figError: '' });
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [summary, snap] = await Promise.all([
          db.collection('engineeringMeta').doc('summary').get(),
          db.collection('engineeringDocs').orderBy('order').get(),
        ]);
        if (!alive) return;
        const docs = [];
        snap.forEach((d) => docs.push({ id: d.id, ...d.data() }));
        setState((s) => ({ ...s, loading: false, summary: summary.exists ? summary.data() : null, docs }));
      } catch (err) {
        if (!alive) return;
        const denied = /permission|insufficient/i.test(err.message || '');
        setState((s) => ({ ...s, loading: false, error: denied ? 'Engineering Handbook access required.' : err.message }));
      }
      if (isBackendConfigured()) {
        try {
          const r = await apiGet('/admin/engineering/figures');
          if (alive) setState((s) => ({ ...s, figures: r.urls || {} }));
        } catch (err) {
          if (alive) setState((s) => ({ ...s, figError: err.message }));
        }
      }
    })();
    return () => { alive = false; };
  }, []);
  return state;
}

function useReference(key) {
  const [state, setState] = useState({ loading: !!key, value: null, error: '' });
  useEffect(() => {
    if (!key) return undefined;
    let alive = true;
    setState({ loading: true, value: null, error: '' });
    db.collection('engineeringMeta').doc(`ref-${REF_DOC[key] || key}`).get()
      .then((d) => { if (alive) setState({ loading: false, value: d.exists ? d.data().value : null, error: d.exists ? '' : 'Not synced yet — run scripts/sync-engineering.mjs.' }); })
      .catch((err) => { if (alive) setState({ loading: false, value: null, error: err.message }); });
    return () => { alive = false; };
  }, [key]);
  return state;
}

// ---- Shared bits ----------------------------------------------------------

function Markdown({ text, docs, figures }) {
  const navigate = useNavigate();
  const components = useMemo(() => ({
    a: ({ href, children }) => {
      if (href && href.startsWith('#')) {
        const anchor = href.slice(1);
        const target = docs.find((d) => d.id.endsWith(`-${anchor}`) || slugify(d.title) === anchor);
        if (target) return <a href={`${BASE}/book/${target.id}`} className="ab-link" onClick={(e) => { e.preventDefault(); navigate(`${BASE}/book/${target.id}`); }}>{children}</a>;
        return <span>{children}</span>;
      }
      if (href && href.startsWith(BRAIN_PATH)) return <a href={href} className="ab-link" onClick={(e) => { e.preventDefault(); navigate(href); }}>{children}</a>;
      const external = /^https?:/i.test(href || '');
      return <a href={href} target={external ? '_blank' : undefined} rel={external ? 'noreferrer' : undefined}>{children}</a>;
    },
    img: ({ src, alt }) => {
      const file = String(src || '').split('/').pop().split(' ')[0];
      const url = figures[file];
      return url ? <img src={url} alt={alt || ''} className="eh-fig" /> : <span className="eh-fig-missing">[figure {file} — not available]</span>;
    },
  }), [docs, figures, navigate]);
  return (
    <div className="ab-md eh-md">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>{wikify(text)}</ReactMarkdown>
    </div>
  );
}

function Figure({ fig, figures, figError }) {
  const url = figures[fig.file];
  return (
    <figure className="eh-figure">
      {url ? <img src={url} alt={fig.caption} /> : <div className="eh-fig-missing">{figError ? `Figures unavailable: ${figError}` : 'Loading figure…'}</div>}
      {fig.caption && <figcaption>{fig.caption}</figcaption>}
    </figure>
  );
}

function SearchBox({ value, onChange, placeholder }) {
  return <input className="cs-input ab-search" type="search" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />;
}

const matches = (q, ...fields) => !q || fields.some((f) => String(f ?? '').toLowerCase().includes(q));

// ---- Views ----------------------------------------------------------------

function Overview({ summary, docs, figError }) {
  const counts = summary?.counts || {};
  const books = summary?.books || [];
  return (
    <>
      <div className="cs-card ab-card">
        <h2>Engineering Handbook <span className="cs-help">synced {fmtAgo(summary?.syncedAt)}{summary?.sha ? ` · ${summary.sha}` : ''}</span></h2>
        <p className="ab-blurb">
          Everything about the platform in one gated place: the books at full fidelity, system diagrams you can click into,
          and a reference generated from the code itself (routes, workers, every config key, every rule). Rebuild with
          <code>node scripts/sync-engineering.mjs</code> after a book or code change — the promote step runs it for production.
        </p>
        {figError && <p className="eh-warn">Figures could not be loaded ({figError}). Text and diagrams still work.</p>}
        <div className="ab-stats">
          <Stat label="Sections" value={counts.sections ?? docs.filter((d) => !d.header).length} to={`${BASE}/book`} />
          <Stat label="Diagrams" value={DIAGRAMS.length} to={`${BASE}/diagrams`} />
          <Stat label="API routes" value={counts.routes ?? '—'} to={`${BASE}/reference/routes`} />
          <Stat label="Workers" value={counts.workers ?? '—'} to={`${BASE}/reference/workers`} />
          <Stat label="Config keys" value={counts.liveConfigKeys ?? '—'} to={`${BASE}/reference/liveConfigKeys`} />
          <Stat label="Collections" value={counts.collections ?? '—'} to={`${BASE}/reference/collections`} />
          <Stat label="Entities" value={counts.entities ?? '—'} to={`${BASE}/reference/entities`} />
          <Stat label="Source files" value={counts.codeFiles ?? '—'} to={`${BASE}/reference/codeMap`} />
        </div>
      </div>
      <div className="ab-grid">
        <div className="ab-col">
          <div className="cs-card ab-card">
            <h2>Books</h2>
            {books.map((b) => {
              const head = docs.find((d) => d.book === b.key && d.header);
              const sections = docs.filter((d) => d.book === b.key && !d.header);
              return (
                <div key={b.key} className="eh-book">
                  <Link to={`${BASE}/book/${b.key}-book`} className="eh-book-title">{head?.title || b.label}</Link>
                  <span className="ab-blurb">{head?.body}</span>
                  <span className="ab-count">{sections.length} sections</span>
                </div>
              );
            })}
          </div>
          <div className="cs-card ab-card">
            <h2>Start here</h2>
            <ul className="ab-plain">
              <li><Link className="ab-link" to={`${BASE}/diagrams/control-plane-topology`}>Control plane topology</Link><span className="ab-blurb">how the console, Firestore, the API, workers and runners fit</span></li>
              <li><Link className="ab-link" to={`${BASE}/diagrams/kit-compose-stack`}>App Kit compose stack</Link><span className="ab-blurb">what one instance is, with no Firebase</span></li>
              <li><Link className="ab-link" to={`${BASE}/diagrams/queue-lifecycle`}>The claim template</Link><span className="ab-blurb">why rolling deploys are safe</span></li>
              <li><Link className="ab-link" to={`${BASE}/diagrams/agentos-gate`}>The agentOS gate</Link><span className="ab-blurb">model → gate → capability → commit</span></li>
              <li><Link className="ab-link" to={`${BASE}/diagrams/release-pipeline`}>Release pipeline</Link><span className="ab-blurb">one build, two planes, one promotion</span></li>
              <li><Link className="ab-link" to={`${BASE}/reference/liveConfigKeys`}>Every live-config key</Link><span className="ab-blurb">what you can change without a deploy</span></li>
            </ul>
          </div>
        </div>
        <div className="ab-col">
          <div className="cs-card ab-card">
            <h2>Diagrams by area</h2>
            {DIAGRAM_GROUPS.map((g) => (
              <div key={g} className="eh-group">
                <h3>{g}</h3>
                <ul className="ab-plain">
                  {DIAGRAMS.filter((d) => d.group === g).map((d) => <li key={d.id}><Link className="ab-link" to={`${BASE}/diagrams/${d.id}`}>{d.title}</Link></li>)}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

function Stat({ label, value, to }) {
  const navigate = useNavigate();
  return (
    <button type="button" className="ab-stat is-clickable" onClick={() => navigate(to)}>
      <span className="ab-stat-value">{value}</span>
      <span className="ab-stat-label">{label}</span>
    </button>
  );
}

function BookIndex({ docs }) {
  const books = [...new Set(docs.map((d) => d.book))];
  return (
    <div className="cs-card ab-card">
      <h2>Books</h2>
      {books.map((b) => {
        const head = docs.find((d) => d.book === b && d.header);
        let part = null;
        return (
          <div key={b} className="eh-toc">
            <h3>{head?.title || b} <span className="cs-help">{head?.body}</span></h3>
            <ol>
              {docs.filter((d) => d.book === b && !d.header).map((d) => {
                const showPart = d.part && d.part !== part;
                part = d.part || part;
                return (
                  <React.Fragment key={d.id}>
                    {showPart && <li className="eh-toc-part">{d.part}</li>}
                    <li><Link to={`${BASE}/book/${d.id}`}>{d.title}</Link>{d.figures?.length ? <span className="ab-count">{d.figures.length} fig</span> : null}{diagramsForSection(d.id).length ? <span className="ab-count">diagram</span> : null}</li>
                  </React.Fragment>
                );
              })}
            </ol>
          </div>
        );
      })}
    </div>
  );
}

function Section({ doc, docs, figures, figError }) {
  const idx = docs.indexOf(doc);
  const prev = idx > 0 ? docs[idx - 1] : null;
  const next = idx >= 0 && idx < docs.length - 1 ? docs[idx + 1] : null;
  const diagrams = diagramsForSection(doc.id);
  const brainSlugs = [...new Set(diagrams.flatMap((d) => d.brain || []))];
  return (
    <>
      <div className="cs-card ab-card eh-section">
        <div className="eh-crumbs"><Link to={`${BASE}/book`}>{doc.bookLabel}</Link>{doc.part ? <> › <span>{doc.part}</span></> : null}</div>
        <h2>{doc.title}</h2>
        {doc.header && <p className="ab-blurb">{doc.body}</p>}
        {!doc.header && doc.figures?.map((f) => <Figure key={f.file} fig={f} figures={figures} figError={figError} />)}
        {!doc.header && <Markdown text={doc.body} docs={docs} figures={figures} />}
        {doc.header && (
          <ol className="eh-toc-inline">
            {docs.filter((d) => d.book === doc.book && !d.header).map((d) => <li key={d.id}><Link to={`${BASE}/book/${d.id}`}>{d.title}</Link></li>)}
          </ol>
        )}
      </div>
      {diagrams.map((d) => (
        <div key={d.id} className="cs-card ab-card">
          <h2>{d.title} <Link className="ab-group-all" to={`${BASE}/diagrams/${d.id}`}>open full size →</Link></h2>
          <p className="ab-blurb">{d.summary}</p>
          <EngineeringFlow spec={d} height={440} compact />
          <FlowLegend />
        </div>
      ))}
      {brainSlugs.length ? (
        <div className="cs-card ab-card">
          <h2>In the brain</h2>
          <div className="ab-linkrow">{brainSlugs.map((s) => <Link key={s} className="ab-link" to={`${BRAIN_PATH}${s}`}>[[{s}]]</Link>)}</div>
        </div>
      ) : null}
      <div className="eh-pager">
        {prev ? <Link to={`${BASE}/book/${prev.id}`}>← {prev.title}</Link> : <span />}
        {next ? <Link to={`${BASE}/book/${next.id}`}>{next.title} →</Link> : <span />}
      </div>
    </>
  );
}

function DiagramsIndex() {
  return (
    <div className="cs-card ab-card">
      <h2>System diagrams <span className="cs-help">{DIAGRAMS.length} · click any node for files, config keys and brain pages</span></h2>
      {DIAGRAM_GROUPS.map((g) => (
        <div key={g} className="eh-group">
          <h3>{g}</h3>
          <div className="eh-diagram-grid">
            {DIAGRAMS.filter((d) => d.group === g).map((d) => (
              <Link key={d.id} to={`${BASE}/diagrams/${d.id}`} className="eh-diagram-card">
                <strong>{d.title}</strong>
                <span>{d.summary}</span>
                <em>{d.nodes.length} nodes · {d.edges.length} edges</em>
              </Link>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function Diagram({ spec, docs }) {
  const section = docs.find((d) => d.id === spec.section || d.id === `platform-${spec.section}`);
  const idx = DIAGRAMS.indexOf(spec);
  const prev = idx > 0 ? DIAGRAMS[idx - 1] : null;
  const next = idx < DIAGRAMS.length - 1 ? DIAGRAMS[idx + 1] : null;
  return (
    <>
      <div className="cs-card ab-card">
        <div className="eh-crumbs"><Link to={`${BASE}/diagrams`}>Diagrams</Link> › <span>{spec.group}</span></div>
        <h2>{spec.title}</h2>
        <p className="ab-blurb">{spec.summary}</p>
        <EngineeringFlow spec={spec} height={600} sectionHref={section ? `${BASE}/book/${section.id}` : null} />
        <FlowLegend />
      </div>
      <div className="ab-grid">
        <div className="cs-card ab-card">
          <h2>Components</h2>
          <ul className="eh-components">
            {spec.nodes.map((nd) => (
              <li key={nd.id}>
                <strong>{nd.label}</strong>
                {nd.what && <span>{nd.what}</span>}
                {nd.files?.length ? <code>{nd.files.join(' · ')}</code> : null}
              </li>
            ))}
          </ul>
        </div>
        <div className="ab-col">
          <div className="cs-card ab-card">
            <h2>Read more</h2>
            <ul className="ab-plain">
              {section && <li><Link className="ab-link" to={`${BASE}/book/${section.id}`}>{section.title}</Link><span className="ab-blurb">{section.bookLabel}</span></li>}
              {(spec.brain || []).map((s) => <li key={s}><Link className="ab-link" to={`${BRAIN_PATH}${s}`}>[[{s}]]</Link><span className="ab-blurb">brain</span></li>)}
            </ul>
          </div>
          <div className="eh-pager">
            {prev ? <Link to={`${BASE}/diagrams/${prev.id}`}>← {prev.title}</Link> : <span />}
            {next ? <Link to={`${BASE}/diagrams/${next.id}`}>{next.title} →</Link> : <span />}
          </div>
        </div>
      </div>
    </>
  );
}

// ---- Reference tables -----------------------------------------------------

function Reference({ tab, q, setQ, summary }) {
  const meta = REFERENCE_TABS.find(([k]) => k === tab) || REFERENCE_TABS[0];
  const { loading, value, error } = useReference(meta[0]);
  const query = q.trim().toLowerCase();
  return (
    <div className="cs-card ab-card">
      <div className="eh-reftabs">
        {REFERENCE_TABS.map(([k, label]) => <Link key={k} to={`${BASE}/reference/${k}`} className={`ab-chip ${k === meta[0] ? 'is-active' : ''}`}>{label}</Link>)}
      </div>
      <h2>{meta[1]} <span className="cs-help">{meta[2]} Generated {fmtAgo(summary?.syncedAt)}{summary?.sha ? ` @ ${summary.sha}` : ''}.</span></h2>
      <SearchBox value={q} onChange={setQ} placeholder={`Filter ${meta[1].toLowerCase()}…`} />
      {loading && <p className="ab-blurb">Loading…</p>}
      {error && <p className="eh-warn">{error}</p>}
      {value && meta[0] === 'routes' && <RoutesTable rows={value} q={query} />}
      {value && meta[0] === 'workers' && <WorkersTable rows={value} q={query} />}
      {value && meta[0] === 'liveConfigKeys' && <ConfigTable value={value} q={query} />}
      {value && meta[0] === 'collections' && <CollectionsTable rows={value} q={query} />}
      {value && meta[0] === 'entities' && <EntitiesTable rows={value} q={query} />}
      {value && meta[0] === 'modules' && <ModulesTable rows={value} q={query} />}
      {value && meta[0] === 'frontendRoutes' && <FrontendRoutesTable rows={value} q={query} />}
      {value && meta[0] === 'scripts' && <FilesTable rows={value} q={query} />}
      {value && meta[0] === 'codeMap' && Object.entries(value).map(([group, rows]) => <FilesTable key={group} title={group} rows={rows} q={query} />)}
    </div>
  );
}

function RoutesTable({ rows, q }) {
  const list = rows.filter((r) => matches(q, r.method, r.path, r.file, r.gate, r.doc));
  return (
    <table className="eh-table">
      <thead><tr><th>Method</th><th>Path</th><th>Gate</th><th>Handler</th><th>Notes</th></tr></thead>
      <tbody>{list.map((r) => (
        <tr key={`${r.method}${r.path}${r.line}`}>
          <td><span className={`eh-method is-${r.method.toLowerCase()}`}>{r.method}</span></td>
          <td><code>{r.path}</code></td>
          <td><span className={`eh-gate ${/public/.test(r.gate) ? 'is-public' : /see file/.test(r.gate) ? 'is-unknown' : ''}`}>{r.gate}</span></td>
          <td><code className="eh-file">{r.file.replace('backend/', '')}:{r.line}</code></td>
          <td className="eh-doc">{r.doc}</td>
        </tr>
      ))}</tbody>
      <tfoot><tr><td colSpan={5}>{list.length} of {rows.length}</td></tr></tfoot>
    </table>
  );
}

function WorkersTable({ rows, q }) {
  const list = rows.filter((r) => matches(q, r.start, r.file, r.doc, r.condition));
  return (
    <table className="eh-table">
      <thead><tr><th>Starts</th><th>Implementation</th><th>Boot condition</th><th>What it does</th></tr></thead>
      <tbody>{list.map((r) => (
        <tr key={r.start}>
          <td><code>{r.start}</code></td>
          <td><code className="eh-file">{r.file || '—'}</code></td>
          <td className="eh-doc">{r.condition ? <code>{r.condition}</code> : 'always'}</td>
          <td className="eh-doc">{r.doc}</td>
        </tr>
      ))}</tbody>
    </table>
  );
}

function ConfigTable({ value, q }) {
  const blocks = Object.fromEntries((value.blocks || []).map((b) => [b.key, b.comment]));
  const keys = (value.keys || []).filter((k) => matches(q, k.key, k.def, k.comment));
  const groups = [...new Set(keys.map((k) => k.key.split('.')[0]))];
  return (
    <>
      <p className="ab-blurb">{keys.length} keys. Edit <code>config/app</code> in Firestore (or the kit’s Dev Console editor); the backend re-reads every minute. A key must exist in <code>CONFIG_DEFAULTS</code> or it is dropped on load.</p>
      {groups.map((g) => (
        <details key={g} className="eh-block" open={!!q || groups.length < 4}>
          <summary><code>{g}</code><span className="ab-blurb">{blocks[g]}</span><span className="ab-count">{keys.filter((k) => k.key.split('.')[0] === g).length}</span></summary>
          <table className="eh-table">
            <thead><tr><th>Key</th><th>Default</th><th>Meaning</th></tr></thead>
            <tbody>{keys.filter((k) => k.key.split('.')[0] === g).map((k) => (
              <tr key={k.key}><td><code>{k.key}</code></td><td><code className="eh-def">{k.def}</code></td><td className="eh-doc">{k.comment}</td></tr>
            ))}</tbody>
          </table>
        </details>
      ))}
    </>
  );
}

function CollectionsTable({ rows, q }) {
  const list = rows.filter((r) => matches(q, r.path, r.read, r.write));
  return (
    <table className="eh-table">
      <thead><tr><th>Path</th><th>Read</th><th>Write</th></tr></thead>
      <tbody>{list.map((r) => (
        <tr key={`${r.path}${r.line}`}>
          <td><code>{r.path}</code><div className="ab-blurb">rules:{r.line}</div></td>
          <td className="eh-rule">{r.read}</td>
          <td className="eh-rule">{r.write}</td>
        </tr>
      ))}</tbody>
    </table>
  );
}

function EntitiesTable({ rows, q }) {
  const list = rows.filter((r) => matches(q, r.name, r.label, r.path, r.notes, (r.fields || []).join(' ')));
  return (
    <div className="eh-entities">
      {list.map((r) => (
        <details key={r.name} className="eh-block" open={!!q}>
          <summary><strong>{r.label}</strong><code>{r.path}</code>{r.entitlement && <span className="ab-chip is-static">entitlement: {r.entitlement}</span>}</summary>
          {r.notes && <p className="ab-blurb">{r.notes}</p>}
          <div className="eh-kv">
            {r.roles && <div><b>Roles</b><code>{JSON.stringify(r.roles)}</code></div>}
            {r.create && <div><b>Create</b><code>{JSON.stringify(r.create)}</code></div>}
          </div>
          <ul className="eh-fields">{(r.fields || []).map((f) => <li key={f}><code>{f}</code></li>)}</ul>
        </details>
      ))}
    </div>
  );
}

function ModulesTable({ rows, q }) {
  const list = rows.filter((r) => matches(q, r.label, r.to, r.tag, r.purpose, (r.tasks || []).map((t) => t.ask).join(' ')));
  return (
    <div className="eh-entities">
      {list.map((r) => (
        <details key={r.to} className="eh-block" open={!!q}>
          <summary><strong>{r.label}</strong><code>{r.to}</code><span className="ab-chip is-static">{r.tag}</span></summary>
          <p className="ab-blurb">{r.purpose}</p>
          {(r.tasks || []).map((t) => (
            <div key={t.ask} className="eh-task">
              <b>“{t.ask}”</b> {t.agent && <span className="ab-chip is-static">{t.agent}</span>} <code>{t.route}</code>
              <ol>{(t.steps || []).map((s) => <li key={s}>{s}</li>)}</ol>
            </div>
          ))}
        </details>
      ))}
    </div>
  );
}

function FrontendRoutesTable({ rows, q }) {
  const list = rows.filter((r) => matches(q, r.path, r.component));
  return (
    <table className="eh-table">
      <thead><tr><th>Path</th><th>Component</th><th>Auth</th></tr></thead>
      <tbody>{list.map((r, i) => <tr key={`${r.path}${i}`}><td><code>{r.path}</code></td><td><code>{r.component}</code></td><td>{r.gated ? <span className="eh-gate">RequireAuth</span> : <span className="eh-gate is-public">public</span>}</td></tr>)}</tbody>
    </table>
  );
}

function FilesTable({ rows, q, title }) {
  const list = rows.filter((r) => matches(q, r.file, r.doc));
  if (!list.length) return null;
  return (
    <div className="eh-files">
      {title && <h3>{title} <span className="ab-count">{list.length}</span></h3>}
      <ul className="ab-files">{list.map((r) => <li key={r.file}><code className="eh-file">{r.file}</code><span className="eh-doc">{r.doc || <em>no header comment</em>}</span></li>)}</ul>
    </div>
  );
}

// ---- Page -----------------------------------------------------------------

export default function AdminEngineering() {
  const { tab = 'overview', slug } = useParams();
  const [params, setParams] = useSearchParams();
  const { loading, error, summary, docs, figures, figError } = useHandbook();
  const [search, setSearch] = useState('');
  const q = params.get('q') || '';
  const setQ = (v) => setParams(v ? { q: v } : {}, { replace: true });
  useEffect(() => { window.scrollTo(0, 0); }, [tab, slug]);

  const bookDocs = docs.filter((d) => !d.header);
  const hits = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return null;
    return {
      sections: bookDocs.filter((d) => matches(s, d.title, d.body)).slice(0, 30),
      diagrams: DIAGRAMS.filter((d) => matches(s, d.title, d.summary, d.nodes.map((nd) => `${nd.label} ${nd.what} ${(nd.files || []).join(' ')}`).join(' '))),
    };
  }, [search, bookDocs]);

  if (loading) return <div className="ab-page"><p className="ab-blurb">Loading the handbook…</p></div>;
  if (error) return <div className="ab-page"><h1>Engineering Handbook</h1><p className="eh-warn">{error}</p></div>;

  const doc = tab === 'book' && slug ? docs.find((d) => d.id === slug) : null;
  const diagram = tab === 'diagrams' && slug ? diagramById(slug) : null;
  let part = null;

  return (
    <div className="ab-page eh-page">
      <h1>Engineering Handbook</h1>
      <p className="cs-help">Deep engineering detail for every part of nventr — books, system diagrams, and a reference generated from the code. Super-admin only.</p>
      <div className="ab-shell">
        <nav className="ab-rail">
          <SearchBox value={search} onChange={setSearch} placeholder="Search sections and diagrams…" />
          {hits ? (
            <div className="ab-nav">
              {hits.diagrams.map((d) => <Link key={d.id} className="ab-nav-item" to={`${BASE}/diagrams/${d.id}`} onClick={() => setSearch('')}>◇ {d.title}</Link>)}
              {hits.sections.map((d) => <Link key={d.id} className="ab-nav-item" to={`${BASE}/book/${d.id}`} onClick={() => setSearch('')}>{d.title}</Link>)}
              {!hits.sections.length && !hits.diagrams.length && <span className="ab-blurb">No matches.</span>}
            </div>
          ) : (
            <>
              <div className="ab-nav">
                <Link className={`ab-nav-item ${tab === 'overview' ? 'is-active' : ''}`} to={BASE}>Overview</Link>
                <Link className={`ab-nav-item ${tab === 'diagrams' ? 'is-active' : ''}`} to={`${BASE}/diagrams`}>Diagrams <span className="ab-count">{DIAGRAMS.length}</span></Link>
                <Link className={`ab-nav-item ${tab === 'reference' ? 'is-active' : ''}`} to={`${BASE}/reference/routes`}>Reference</Link>
              </div>
              {(summary?.books || []).map((b) => (
                <details key={b.key} className="ab-group" open={!doc || doc.book === b.key}>
                  <summary><strong>{b.label}</strong><Link className="ab-group-all" to={`${BASE}/book/${b.key}-book`}>contents</Link></summary>
                  <ul className="ab-group-list">
                    {bookDocs.filter((d) => d.book === b.key).map((d) => {
                      const showPart = d.part && d.part !== part;
                      part = d.part || part;
                      return (
                        <React.Fragment key={d.id}>
                          {showPart && <li className="eh-rail-part">{d.part.replace(/^Part\s+\w+\s+—\s+/, '')}</li>}
                          <li className={doc?.id === d.id ? 'is-active' : ''}><Link to={`${BASE}/book/${d.id}`}>{d.title}</Link></li>
                        </React.Fragment>
                      );
                    })}
                  </ul>
                </details>
              ))}
            </>
          )}
        </nav>
        <main className="ab-main">
          {tab === 'overview' && <Overview summary={summary} docs={docs} figError={figError} />}
          {tab === 'book' && !slug && <BookIndex docs={docs} />}
          {tab === 'book' && slug && (doc ? <Section doc={doc} docs={docs} figures={figures} figError={figError} /> : <p className="eh-warn">No such section.</p>)}
          {tab === 'diagrams' && !slug && <DiagramsIndex />}
          {tab === 'diagrams' && slug && (diagram ? <Diagram spec={diagram} docs={docs} /> : <p className="eh-warn">No such diagram.</p>)}
          {tab === 'reference' && <Reference tab={slug || 'routes'} q={q} setQ={setQ} summary={summary} />}
        </main>
      </div>
    </div>
  );
}
